import React, { useEffect, useMemo, useState } from "react";
import { Convert, ReservationElement as Reservation } from "@/models/responses";
import { Platform, YearAndPeriod } from "@/models/";
import { useAppData } from "@/hooks/useAppData";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { partitionReservations } from "./reportReservations.partition";

interface ReportReservationProps {
    yearAndPeriod: YearAndPeriod;
}

type ApartmentEntities = ReturnType<typeof useActiveApartmentEntities>;

/**
 * One reservations table (header + rows). Shared by the Bookings and
 * Cancellations sections so both render identical columns; the Cancellations
 * table is rendered `muted` to read as subordinate to the bookings above it.
 */
const ReservationsTable: React.FC<{
    caption: string;
    reservations: Reservation[];
    apartmentEntities: ApartmentEntities;
    muted?: boolean;
}> = ({ caption, reservations, apartmentEntities, muted = false }) => (
    <div className="w-full report-table-wrapper">
        <Table className={`text-[11px] report-table${muted ? " text-muted-foreground" : ""}`}>
            {/* sr-only caption gives each table a distinct accessible name
                (WCAG 1.3.1) without changing the visual layout; the visible
                "Cancellations" heading below covers sighted users. */}
            <caption className="sr-only">{caption}</caption>
            <TableHeader className="bg-muted">
                <TableRow className="hover:bg-muted">
                    <TableHead className="text-left py-2 px-1">Platform</TableHead>
                    <TableHead className="text-left py-2 px-1">Code</TableHead>
                    <TableHead className="text-left py-2 px-1">Guest</TableHead>
                    <TableHead className="text-left py-2 px-1">#Guests</TableHead>
                    <TableHead className="text-left py-2 px-1">Start</TableHead>
                    <TableHead className="text-left py-2 px-1">End</TableHead>
                    <TableHead className="text-left py-2 px-1">#Days</TableHead>
                    <TableHead className="text-left py-2 px-1">Apartment</TableHead>
                    <TableHead className="text-right py-2 px-1 whitespace-nowrap">Gross Inc</TableHead>
                    <TableHead className="text-right py-2 px-1">Comm</TableHead>
                    <TableHead className="text-right py-2 px-1">Tax</TableHead>
                    <TableHead className="text-right py-2 px-1">Clean</TableHead>
                    <TableHead className="text-right py-2 px-1 bg-muted font-bold whitespace-nowrap">Net Inc</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {reservations.map((reservation) => (
                    <TableRow key={reservation.id} className="hover:bg-muted">
                        <TableCell className="py-2 px-1">{Platform[reservation.platform]}</TableCell>
                        <TableCell className="py-2 px-1">{reservation.reservationCode}</TableCell>
                        <TableCell className="py-2 px-1">{reservation.guestName}</TableCell>
                        <TableCell className="py-2 px-1">{reservation.numberOfGuests}</TableCell>
                        <TableCell className="py-2 px-1 whitespace-nowrap">{reservation.startDate.toISOString().substring(0, 10)}</TableCell>
                        <TableCell className="py-2 px-1 whitespace-nowrap">{reservation.endDate.toISOString().substring(0, 10)}</TableCell>
                        <TableCell className="py-2 px-1">{reservation.actualDaysOfStay}</TableCell>
                        <TableCell className="py-2 px-1">{apartmentEntities[reservation.apartmentId].shortName || "N/A"}</TableCell>
                        <TableCell className="text-right py-2 px-1 whitespace-nowrap">{reservation.receivableIncome.toFixed(2)} €</TableCell>
                        <TableCell className="text-right py-2 px-1 whitespace-nowrap">{reservation.commission.toFixed(2)} €</TableCell>
                        <TableCell className="text-right py-2 px-1 whitespace-nowrap">
                            {(reservation.temporaryStayDuty + reservation.sustainabilityFee + reservation.vat).toFixed(2)} €
                        </TableCell>
                        <TableCell className="text-right py-2 px-1 whitespace-nowrap">{reservation.cleaningFee?.toFixed(2)} €</TableCell>
                        <TableCell className="text-right py-2 px-1 bg-muted font-bold whitespace-nowrap">
                            {(
                                reservation.receivableIncome -
                                reservation.commission -
                                (reservation.cleaningFee || 0) -
                                reservation.vat -
                                reservation.temporaryStayDuty -
                                reservation.sustainabilityFee
                            ).toFixed(2)}{" "}
                            €
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </div>
);

const ReportReservations: React.FC<ReportReservationProps> = ({ yearAndPeriod }) => {
    const { selectedOwner } = useAppData();
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const apartmentEntities = useActiveApartmentEntities();

    useEffect(() => {
        fetch(
            `${apiUrl}${getApiBasePath()}/owners/${selectedOwner}/reservations?year=${yearAndPeriod.year}&period=${yearAndPeriod.period}&startingInPeriod=true`,
            fetchOptions,
        )
            .then((response) => {
                if (response) {
                    return response.text();
                }
                throw new Error("Unauthorized");
            })
            .then((data) => {
                const reservations = Convert.toGetReservationsResponse(data).reservations;
                const sortedReservations = reservations.sort((a, b) => {
                    if (a.apartmentId !== b.apartmentId) {
                        return a.apartmentId - b.apartmentId;
                    }
                    return a.startDate.getTime() - b.startDate.getTime();
                });
                setReservations(sortedReservations);
            })
            .catch((error) =>
                toast({
                    title: "Error",
                    description: `Failed to fetch reservations: ${error.message}`,
                    variant: "destructive",
                }),
            );
    }, [yearAndPeriod, selectedOwner]);

    // Cancellations live in their own section after the bookings so the owner
    // sees that there were cancellations without them being mixed in.
    const { bookings, cancellations } = useMemo(() => partitionReservations(reservations), [reservations]);

    if (bookings.length === 0 && cancellations.length === 0) {
        return <p className="text-sm text-muted-foreground">No reservations for this period.</p>;
    }

    return (
        <>
            <ReservationsTable caption="Bookings" reservations={bookings} apartmentEntities={apartmentEntities} />
            {cancellations.length > 0 && (
                <div className="mt-8">
                    <h3 className="text-lg font-semibold text-foreground mb-3">Cancellations</h3>
                    <ReservationsTable caption="Cancellations" reservations={cancellations} apartmentEntities={apartmentEntities} muted />
                </div>
            )}
        </>
    );
};

export default ReportReservations;
